import { t as uiText } from '../../i18n';
import { extensionOf, resolveUploadContentType } from '../../utils/storageContentType';

/**
 * スレッド添付のアップロード前検証（UX 改善用）。
 *
 * ⚠ 正本は Storage Rules（storage.rules）とサーバー（threadTempQuota.ts）。
 *   ここは「アップロードしてから弾かれる」を避けるための事前チェックで、Rules を緩めるものではない。
 *   Rules / サーバー側の許可条件を変えたら、この定数も同じ内容に合わせること。
 */

export const MAX_PERMANENT_ATTACHMENT_BYTES = 100 * 1024 * 1024; // storage.rules: files/ 100MB
export const MAX_TEMPORARY_ATTACHMENT_BYTES = 1024 * 1024 * 1024; // threadTempQuota: 1GB/file

// storage.rules hasAllowedFileExt
const PERMANENT_ALLOWED_EXTENSIONS = new Set([
  'pdf', 'txt', 'md', 'csv', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'mp4', 'mov', 'webm',
]);

// storage.rules isAllowedFileContentType
const PERMANENT_ALLOWED_MIME = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'application/pdf',
  'text/plain', 'text/markdown', 'text/x-markdown', 'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'video/mp4', 'video/quicktime', 'video/webm',
]);

// storage.rules hasOctetStreamExt（octet-stream を許すのはこれらの拡張子だけ）
const OCTET_STREAM_ALLOWED_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv',
]);

// functions/src/threads/threadTempQuota.ts BLOCKED_EXTENSIONS / BLOCKED_MIME
const TEMP_BLOCKED_EXTENSIONS = new Set([
  'html', 'htm', 'xhtml', 'svg', 'exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'dll', 'sh', 'jar', 'ps1', 'vbs',
]);
const TEMP_BLOCKED_MIME_EXACT = new Set([
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-sh',
  'application/x-executable',
]);
// storage.rules isSafeTempContentType（前方一致）
const TEMP_BLOCKED_MIME_PREFIXES = ['text/html', 'application/xhtml', 'image/svg'];

export type AttachmentKind = 'permanent' | 'temporary';

export type AttachmentRejectCode =
  | 'too_large'
  | 'empty'
  | 'unsupported_extension'
  | 'unsupported_type'
  | 'blocked_type';

export type AttachmentValidation =
  | { ok: true; code?: undefined; message?: undefined }
  | { ok: false; code: AttachmentRejectCode; message: string };

const reject = (code: AttachmentRejectCode, message: string): AttachmentValidation => ({ ok: false, code, message });

type FileLike = { name: string; size: number; type?: string };

export function validateAttachmentFile(file: FileLike, kind: AttachmentKind): AttachmentValidation {
  const ext = extensionOf(file.name);

  if (kind === 'temporary') {
    if (!(file.size > 0)) return reject('empty', uiText("The file is empty."));
    if (file.size > MAX_TEMPORARY_ATTACHMENT_BYTES) {
      return reject('too_large', uiText("Over the 1 GB limit. Compress or split the file and try again."));
    }
    const mime = (file.type || '').toLowerCase();
    if (
      TEMP_BLOCKED_EXTENSIONS.has(ext) ||
      TEMP_BLOCKED_MIME_EXACT.has(mime) ||
      TEMP_BLOCKED_MIME_PREFIXES.some((p) => mime.startsWith(p))
    ) {
      return reject('blocked_type', uiText("HTML, SVG and executable files can't be attached."));
    }
    return { ok: true };
  }

  if (file.size > MAX_PERMANENT_ATTACHMENT_BYTES) {
    return reject(
      'too_large',
      uiText("Over the 100 MB permanent file limit. Attach it as a temporary file to allow up to 1 GB.")
    );
  }
  if (!PERMANENT_ALLOWED_EXTENSIONS.has(ext)) {
    return reject(
      'unsupported_extension',
      uiText("This file type isn't supported. Supported: PDF, Office, text/CSV/Markdown, images (JPEG/PNG/WebP/GIF), MP4/MOV/WebM.")
    );
  }
  // 実際のアップロードで Storage に送る contentType（空/octet-stream は拡張子から補完される）
  const resolved = (resolveUploadContentType(file) || 'application/octet-stream').toLowerCase();
  const mimeOk =
    PERMANENT_ALLOWED_MIME.has(resolved) ||
    (resolved === 'application/octet-stream' && OCTET_STREAM_ALLOWED_EXTENSIONS.has(ext));
  if (!mimeOk) {
    return reject('unsupported_type', uiText("The file's content type isn't supported."));
  }
  return { ok: true };
}

export interface RejectedAttachment {
  name: string;
  code: AttachmentRejectCode;
  message: string;
}

/** 複数選択から、非対応ファイルだけを除外する（対応ファイルはそのまま返す）。 */
export function partitionAttachmentFiles<T extends FileLike>(
  files: T[],
  kind: AttachmentKind
): { accepted: T[]; rejected: RejectedAttachment[] } {
  const accepted: T[] = [];
  const rejected: RejectedAttachment[] = [];
  for (const f of files) {
    const v = validateAttachmentFile(f, kind);
    if (v.ok) accepted.push(f);
    else rejected.push({ name: f.name, code: v.code, message: v.message });
  }
  return { accepted, rejected };
}

/** 送信（アップロード）直前の検証エラー。UI から直接呼ばれなかった場合の安全網。 */
export class AttachmentValidationError extends Error {
  readonly fileName: string;
  readonly code: AttachmentRejectCode;
  readonly reason: string;
  constructor(fileName: string, code: AttachmentRejectCode, reason: string) {
    super(`${fileName}: ${reason}`);
    this.name = 'AttachmentValidationError';
    this.fileName = fileName;
    this.code = code;
    this.reason = reason;
  }
}

/** 1 つでも非対応があれば、何もアップロードする前に例外を投げる。 */
export function assertAttachmentFilesValid(items: Array<{ file: FileLike; kind: AttachmentKind }>): void {
  for (const { file, kind } of items) {
    const v = validateAttachmentFile(file, kind);
    if (!v.ok) throw new AttachmentValidationError(file.name, v.code, v.message);
  }
}

/** スレッド添付では既存ファイルの上書きを許可しない。 */
export class ThreadOverwriteNotAllowedError extends Error {
  constructor() {
    super('THREAD_ATTACHMENT_OVERWRITE_NOT_ALLOWED');
    this.name = 'ThreadOverwriteNotAllowedError';
  }
}

/** 上書き指定が 1 件でもあれば、何もアップロード・更新する前に拒否する（別名保存へは自動変換しない）。 */
export function assertNoThreadOverwrite(
  pendingFiles: Array<{ resolution?: { kind: string } | null }>
): void {
  if (pendingFiles.some((pf) => pf.resolution?.kind === 'overwrite-existing')) {
    throw new ThreadOverwriteNotAllowedError();
  }
}

export function formatRejectedAttachments(rejected: RejectedAttachment[]): string {
  const lines = rejected.map((r) => `・${r.name}: ${r.message}`).join('\n');
  return `${uiText("These files can't be attached and were skipped:")}\n\n${lines}`;
}
